/**
 * uploadWithProgress — XHR-based file upload with percentage callback.
 *
 * fetch() doesn't support upload progress. XMLHttpRequest does via
 * xhr.upload.onprogress. Returns a promise that resolves with the parsed
 * JSON response or rejects with an error.
 *
 * @param {string} url - Upload endpoint
 * @param {FormData|File} body - FormData to send (or single File wrapped in FormData)
 * @param {function} onProgress - Called with 0-100 integer percentage
 * @param {number} serverProcessingPct - What % to reserve for server processing (default 10 = 0-90% upload, 90-100% server)
 */
export function uploadWithProgress(url, body, onProgress, serverProcessingPct = 10) {
  const uploadCeil = 100 - serverProcessingPct;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const pct = Math.round((e.loaded / e.total) * uploadCeil);
        onProgress(pct);
      }
    });

    xhr.upload.addEventListener('load', () => {
      // Upload done, server processing now
      onProgress?.(uploadCeil);
    });

    xhr.addEventListener('load', () => {
      onProgress?.(100);
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
        } else {
          // FastAPI sends a string for HTTPException and a list for 422s
          const detail = typeof data?.detail === 'string' ? data.detail : null;
          reject(new Error(detail || `Upload failed (${xhr.status})`));
        }
      } catch {
        reject(new Error(`Upload failed (${xhr.status})`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

    xhr.open('POST', url);
    xhr.send(body);
  });
}

// Convenience alias for video (reserves 10% for FFmpeg compression)
export function uploadVideoWithProgress(url, file, onProgress) {
  const formData = new FormData();
  formData.append('files', file);
  return uploadWithProgress(url, formData, onProgress, 10);
}
